import React, { useEffect, useState } from "react";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import CustomTextArea from "../../../../components/common/CustomTextArea/CustomTextArea";
import CustomFileUpload from "../../../../components/common/CustomFileUpload/CustomFileUpload";
import BodyHeader from "../../../../components/common/CommonBodyHeader";
import CustomDropdown from "../../../../components/common/CustomDropdown/CustomDropdown";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { addCustomerMasterAsync } from "../../../../redux/features/customerMaster";
import { getFleetMasterDropdownDataAsync } from "../../../../redux/features/fleetMaster";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import RouteNames from "../../../../AppRoutes/RouteName";
import validation from "../../../../helpers/validation";
import {
   isValidFileType,
   MAX_FILE_SIZE,
   MAX_FILE_SIZE_MESSAGE,
} from "../../../../helpers/fileValidationRules";
import { MAX_DATE } from "../../../../Hooks/dateValidationRules";
import CustomDatePicker from "../../../../components/common/CustomDatePicker/CustomDatePicker";

const validationSchema = Yup.object().shape({
   customerName: validation.name("Customer Name"),
   gstNumber: validation.gstNumber.notRequired(),
   // agreementDocument: Yup.array().of(validation.file("Agreement Document")),
   agreementDetails: Yup.array().of(
      Yup.object().shape({
         startDate: Yup.date()
            .required("Start Date is required")
            .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
         endDate: Yup.date()
            .required("End Date is required")
            .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
         agreementDocument: Yup.mixed()
            .required("Agreement document is required")
            .test("is-valid-type", "Not a valid document type", (value) => {
               return isValidFileType(value && value.name.toLowerCase(), "all");
            })
            .test(
               "is-valid-size",
               MAX_FILE_SIZE_MESSAGE,
               (value) => value && value.size <= MAX_FILE_SIZE
            ),
      })
   ),
   contactPerson: Yup.array().of(
      Yup.object().shape({
         name: validation.name("Contact Person Name"),
         mobile: validation.mobile,
         email: validation.email,
      })
   ),
   //  location: Yup.array().of(Yup.string().required("")),
   address: Yup.array().of(validation.address()),
   lane: Yup.array().of(
      Yup.object().shape({
         laneName: Yup.string(),
         origin: Yup.string().required("Origin is required"),
         destination: Yup.string().required("Destination is required"),
         vehicleType: Yup.string().required("Vehicle Type is required"),
         tonnage: Yup.string().required("Tonnage is required"),
         expressModeRateAdditional: validation.amount("express mode rate additional"),
         superExpressModeRateAdditional: validation.amount(
            "super express mode rate additional"
         ),
         detentionRateAdditional: validation.amount("detention rate additional"),
         multipleLoadingLocationRateAdditional: validation.amount(
            "multiple loading location rate additional"
         ),
         multipleUnloadingLocationRateAdditional: validation.amount(
            "multiple unloading location rate additional"
         ),
         loadingCharges: validation.amount("loading Charges"),
         unloadingCharge: validation.amount("unloading Charges"),
         miscellaneousCharges: validation.amount("miscellaneous charges"),
         brokerageCharges: validation.amount("brokerage charges"),
         miscellaneousRemarks: Yup.string().required("Miscellaneous Remarks is required"),
         tripRate: validation.amount("trip rate"),

         // New fields
         dieselRate: validation.amount("diesel rate"), // Assuming validation.amount is defined for validation
         adblueRate: validation.amount("adblue rate"),
         tripAdvance: validation.amount("trip advance"),
         miscellaneousRate: validation.amount("miscellaneous rate"),
      })
   ),
});

const initialValues = {
   customerName: "",
   gstNumber: "",
   agreementDetails: [{ startDate: "", endDate: "", agreementDocument: null }],
   contactPerson: [
      {
         name: "",
         mobile: "",
         email: "",
      },
   ],
   location: [""],
   address: [""],
   lane: [
      {
         laneName: "",
         origin: "",
         destination: "",
         vehicleType: "",
         tonnage: "",
         expressModeRateAdditional: "",
         superExpressModeRateAdditional: "",
         detentionRateAdditional: "",
         multipleLoadingLocationRateAdditional: "",
         multipleUnloadingLocationRateAdditional: "",
         loadingCharges: "",
         unloadingCharge: "",
         miscellaneousCharges: "",
         brokerageCharges: "",
         miscellaneousRemarks: "",
         tripRate: "", //
         // New fields
         dieselRate: "", // Add default values or API pending
         adblueRate: "", // Add default values or API pending
         tripAdvance: "", // Add default values or API pending
         miscellaneousRate: "", // Add default values or API pending
      },
   ],
};

export default function CustomerMasterAdd() {
   const navigate = useNavigate();
   const dispatch = useDispatch();

   const fleetMaster = useSelector((state) => state.fleetMaster);
   const locationData = fleetMaster?.location?.map?.((item) => ({
      label: item?.name,
      value: item?.id,
   }));
   const vehicleTypeData = fleetMaster?.vehicleCategory?.map?.((item) => ({
      label: item?.category_name,
      value: item?.id,
   }));
   const tonnageData = fleetMaster?.tonnage?.map?.((item) => ({
      label: item?.tonnage,
      value: item?.id,
   }));

   const [showLaneDetails, setShowLaneDetails] = useState("");
   const [showLaneNameTop, setShowLaneNameTop] = useState(0);
   const [showLaneTable, setShowLaneTable] = useState(false);

   useEffect(() => {
      dispatch(getFleetMasterDropdownDataAsync());
   }, []);

   const handleAddCustomerMaster = (formData) => {
      console.log({ formData });

      const newData = new FormData();

      // Append basic information
      newData.append("name", formData?.customerName);
      newData.append("gst_number", formData?.gstNumber);
      newData.append("model_id", 6);
      newData.append("action_id", 1);

      // Prepare data for the request
      const data = {
         location_master_id: formData?.location?.map?.((item) => +item),
         address: formData.address,
         contact_person_name: formData?.contactPerson?.map?.((item) => item?.name),
         mobile: formData?.contactPerson?.map?.((item) => item?.mobile),
         email: formData?.contactPerson?.map?.((item) => item?.email),
         lane_name: formData?.lane?.map?.((item) => item?.laneName),
         origin: formData?.lane?.map?.((item) => +item?.origin),
         destination: formData?.lane?.map?.((item) => +item?.destination),
         vehicle_category_id: formData?.lane?.map?.((item) => +item?.vehicleType),
         tonnage_id: formData?.lane?.map?.((item) => +item?.tonnage),
         cust_express_mode_rate_additional: formData?.lane?.map?.(
            (item) => +item?.expressModeRateAdditional
         ),
         cust_super_express_mode_rate_additional: formData?.lane?.map?.(
            (item) => +item?.superExpressModeRateAdditional
         ),
         cust_detention_rate_additional: formData?.lane?.map?.(
            (item) => +item?.detentionRateAdditional
         ),
         cust_multiple_loading_location_rate_additional: formData?.lane?.map?.(
            (item) => +item?.multipleLoadingLocationRateAdditional
         ),
         cust_loading_charges: formData?.lane?.map?.((item) => +item?.loadingCharges),
         cust_miscellaneous_charges: formData?.lane?.map?.(
            (item) => +item?.miscellaneousCharges
         ),
         cust_brokerage_charges: formData?.lane?.map?.((item) => +item?.brokerageCharges),
         cust_unloading_charge: formData?.lane?.map?.((item) => +item?.unloadingCharge),
         cust_miscellaneous_remarks: formData?.lane?.map?.(
            (item) => item?.miscellaneousRemarks
         ),
         cust_multiple_unloading_location_rate_additional: formData?.lane?.map?.(
            (item) => +item?.multipleUnloadingLocationRateAdditional
         ),
         cust_trip_rate: formData?.lane?.map?.((item) => +item?.tripRate),

         // New fields
         diesel_rate: formData?.lane?.map?.((item) => +item?.dieselRate),
         adblue_rate: formData?.lane?.map?.((item) => +item?.adblueRate),
         trip_advance: formData?.lane?.map?.((item) => +item?.tripAdvance),
         miscellaneous_rate: formData?.lane?.map?.((item) => +item?.miscellaneousRate),
      };

      const agreementStartDate = formData?.agreementDetails?.map?.(
         (item) => item?.startDate
      );
      const agreementEndDate = formData?.agreementDetails?.map?.((item) => item?.endDate);

      //       console.log({agreementStartDate, agreementEndDate});
      // return

      // Append files to FormData
      formData?.agreementDetails?.forEach?.((item) => {
         newData.append("agreement_document[]", item?.agreementDocument);
      });
      newData.append("start_date[]", agreementStartDate);
      newData.append("end_date[]", agreementEndDate);

      // Append all other data
      Object.keys(data).forEach((key) => {
         data[key]?.forEach((value) => {
            newData.append(`${key}[]`, value);
         });
      });

      // Dispatch action with the FormData
      dispatch(backdropLoadingAction(true));
      dispatch(
         addCustomerMasterAsync({
            data: newData,
            err: () => {
               toast.error("Something went wrong !");
               dispatch(backdropLoadingAction(false));
            },
            done: () => {
               toast.success("Customer master created successfully !");
               dispatch(backdropLoadingAction(false));
               navigate(RouteNames.customerMasterView);
            },
         })
      );
   };

   const formik = useFormik({
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: handleAddCustomerMaster,
   });

   const {
      touched,
      errors,
      values,
      getFieldProps,
      handleSubmit,
      resetForm,
      setFieldValue,
      isValid,
      setFieldTouched,
      isSubmitting,
   } = formik;

   console.log({ values });

   // Handle Lane name save
   const handelLaneSave = (index) => {
      const { origin, destination, tonnage, vehicleType } = values?.lane?.[index];
      if (origin && destination && vehicleType && tonnage && values.customerName) {
         const laneName =
            values.customerName +
            "#" +
            getlabelsFromDropdownData(values.lane?.[index].origin, locationData) +
            "#" +
            getlabelsFromDropdownData(values.lane?.[index].destination, locationData) +
            "#" +
            getlabelsFromDropdownData(values.lane?.[index].vehicleType, vehicleTypeData) +
            "#" +
            getlabelsFromDropdownData(values.lane?.[index].tonnage, tonnageData);
         setFieldValue(`lane[${index}].laneName`, laneName);
         setShowLaneNameTop(index);
         setShowLaneDetails(index);
      } else {
         setFieldTouched(`customerName`);
         setFieldTouched(`lane[${index}].origin`);
         setFieldTouched(`lane[${index}].destination`);
         setFieldTouched(`lane[${index}].tonnage`);
         setFieldTouched(`lane[${index}].vehicleType`);
      }
   };

   const handelSave = (index) => {
      const isvalidLane = Object.values(values?.lane?.[index])?.every(
         (value) => value !== ""
      );
      if (isvalidLane) {
         setShowLaneNameTop("");
         setShowLaneDetails("");
         setShowLaneTable(true);
      } else {
         Object.keys(values?.lane?.[index]).forEach((key) => {
            setFieldTouched(`lane[${index}].${key}`);
         });
      }
   };

   const handelLaneBack = () => {
      setShowLaneDetails(false);
      setShowLaneNameTop(true);
   };

   // Hnalde Add New Lane
   const handleAddNewLane = () => {
      setFieldValue("lane", [
         ...values.lane,
         {
            laneName: "",
            origin: "",
            destination: "",
            vehicleType: "",
            tonnage: "",
            expressModeRateAdditional: "",
            superExpressModeRateAdditional: "",
            detentionRateAdditional: "",
            multipleLoadingLocationRateAdditional: "",
            multipleUnloadingLocationRateAdditional: "",
            loadingCharges: "",
            unloadingCharge: "",
            miscellaneousCharges: "",
            brokerageCharges: "",
            miscellaneousRemarks: "",
            tripRate: "",
         },
      ]);
      setShowLaneNameTop(values.lane.length);
      // setnewLaneDetails(values.lane.length - 1);
   };
   return (
      <FormikProvider value={formik}>
         <div>
            <BodyHeader title="Add Customer Master" />
            {/* <form className="p-3 shadow-lg" onSubmit={formik.handleSubmit}> */}
            <div className="p-3 shadow-lg">
               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Customer Documents</h3>
                  </div>
                  <div className="card-body">
                     <div className="row">
                        <div className="col-lg-6">
                           <CustomInput
                              require
                              label="Customer Name"
                              id="customerName"
                              placeholder="Enter Customer Name"
                              {...getFieldProps("customerName")}
                              errors={
                                 Boolean(touched.customerName) &&
                                 Boolean(errors.customerName)
                              }
                              message={errors.customerName}
                           />
                        </div>
                        <div className="col-lg-6">
                           <CustomInput
                              label="GST Number"
                              id="gstNumber"
                              name="gstNumber"
                              placeholder="Enter GST Number"
                              {...getFieldProps(`gstNumber`)}
                              errors={
                                 Boolean(touched.gstNumber) && Boolean(errors.gstNumber)
                              }
                              message={errors.gstNumber}
                           />
                        </div>

                        {/* Contact Person Details  */}
                        <div className="col-lg-12">
                           <FieldArray
                              name="contactPerson"
                              render={(arrayHelpers) => (
                                 <div>
                                    {values.contactPerson.map((item, index) => (
                                       <div
                                          className={
                                             index < values.contactPerson.length - 1 &&
                                             values.contactPerson.length > 1
                                                ? "row border-bottom mb-4 pb-3"
                                                : "row"
                                          }
                                       >
                                          <div className="col-lg-4">
                                             <CustomInput
                                                require
                                                label="Contact Person Name"
                                                id="contactPersonName"
                                                placeholder="Enter Contact Person Name"
                                                {...getFieldProps(
                                                   `contactPerson[${index}].name`
                                                )}
                                                errors={
                                                   Boolean(
                                                      touched.contactPerson?.[index]?.name
                                                   ) &&
                                                   Boolean(
                                                      errors.contactPerson?.[index]?.name
                                                   )
                                                }
                                                message={
                                                   errors.contactPerson?.[index]?.name
                                                }
                                             />
                                          </div>
                                          <div className="col-lg-4">
                                             <CustomInput
                                                require
                                                label="Customer Mobile No"
                                                id="customerMobileNo"
                                                placeholder="Enter Customer Mobile No"
                                                {...getFieldProps(
                                                   `contactPerson[${index}].mobile`
                                                )}
                                                errors={
                                                   Boolean(
                                                      touched.contactPerson?.[index]
                                                         ?.mobile
                                                   ) &&
                                                   Boolean(
                                                      errors.contactPerson?.[index]
                                                         ?.mobile
                                                   )
                                                }
                                                message={
                                                   errors.contactPerson?.[index]?.mobile
                                                }
                                             />
                                          </div>
                                          <div className="col-lg-4">
                                             <CustomInput
                                                require
                                                label="Customer Email Id"
                                                id="customerEmailId"
                                                placeholder="Enter Customer Email Id"
                                                {...getFieldProps(
                                                   `contactPerson[${index}].email`
                                                )}
                                                errors={
                                                   Boolean(
                                                      touched.contactPerson?.[index]
                                                         ?.email
                                                   ) &&
                                                   Boolean(
                                                      errors.contactPerson?.[index]?.email
                                                   )
                                                }
                                                message={
                                                   errors.contactPerson?.[index]?.email
                                                }
                                             />
                                          </div>
                                          <div className="col-12">
                                             {values.contactPerson.length > 1 &&
                                                index !== 0 && (
                                                   <button
                                                      type="button"
                                                      className="btn btn-danger float-right mr-3"
                                                      onClick={() => {
                                                         arrayHelpers.remove(index);
                                                      }}
                                                   >
                                                      <i className="fas fa-trash"></i>{" "}
                                                      Delete
                                                   </button>
                                                )}
                                          </div>
                                       </div>
                                    ))}
                                 </div>
                              )}
                           />
                        </div>

                        <div className="col-lg-6">
                           {/* Location  */}
                           <div className="">
                              <FieldArray
                                 name="location"
                                 render={(arrayHelpers) => (
                                    <div>
                                       {values.location.map((item, index) => (
                                          <div key={index}>
                                             <CustomDropdown
                                                optionData={locationData}
                                                label="Location"
                                                id=""
                                                onChange={(values) => {
                                                   setFieldValue(
                                                      `location[${index}]`,
                                                      values?.[0]?.value
                                                   );
                                                }}
                                                errors={
                                                   Boolean(touched.location?.[index]) &&
                                                   Boolean(errors.location?.[index])
                                                }
                                                message={errors.location?.[index]}
                                             />
                                             {values.location.length > 1 &&
                                                index !== 0 && (
                                                   <button
                                                      type="button"
                                                      className="btn btn-danger float-right mb-3"
                                                      onClick={() => {
                                                         arrayHelpers.remove(index);
                                                      }}
                                                   >
                                                      <i className="fas fa-trash"></i>{" "}
                                                      Delete Location
                                                   </button>
                                                )}
                                          </div>
                                       ))}
                                    </div>
                                 )}
                              />
                           </div>
                        </div>

                        <div className="col-lg-6">
                           {/* Address  */}
                           <div className="d-block">
                              <FieldArray
                                 name="address"
                                 render={(arrayHelpers) => (
                                    <div>
                                       {values.address.map((item, index) => (
                                          <>
                                             <CustomTextArea
                                                label="Address"
                                                id=""
                                                placeholder="Enter Address "
                                                {...getFieldProps(`address[${index}]`)}
                                                errors={
                                                   Boolean(touched.address?.[index]) &&
                                                   Boolean(errors.address?.[index])
                                                }
                                                message={errors.address?.[index]}
                                             />
                                             {values.address.length > 1 &&
                                                index !== 0 && (
                                                   <button
                                                      type="button"
                                                      className="btn btn-danger float-right mb-3"
                                                      onClick={() => {
                                                         arrayHelpers.remove(index);
                                                      }}
                                                   >
                                                      <i className="fas fa-trash"></i>{" "}
                                                      Delete Address
                                                   </button>
                                                )}
                                          </>
                                       ))}
                                    </div>
                                 )}
                              />
                           </div>
                        </div>

                        <div className="col-12 mt-4">
                           <button
                              type="button"
                              className="btn mx-1 btn-primary"
                              onClick={() => {
                                 setFieldValue("contactPerson", [
                                    ...values.contactPerson,
                                    {
                                       name: "",
                                       mobile: "",
                                       email: "",
                                    },
                                 ]);
                              }}
                           >
                              <i className="fas fa-plus"></i> Add Contact Person
                           </button>
                           <button
                              type="button"
                              className="btn mx-1 btn-primary"
                              onClick={() => {
                                 setFieldValue("address", [...values.address, ""]);
                              }}
                           >
                              <i className="fas fa-plus"></i> Add Address
                           </button>
                           <button
                              type="button"
                              className="btn mx-1 btn-primary"
                              onClick={() => {
                                 setFieldValue("location", [...values.location, ""]);
                              }}
                           >
                              <i className="fas fa-plus"></i> Add Location
                           </button>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="row">
                  <div className="col-lg-12">
                     <div className="card card-primary">
                        <div className="card-header">
                           <h3 className="card-title">Agreement Details</h3>
                        </div>
                        <div className="card-body row">
                           <div className="col-lg-12">
                              <FieldArray
                                 name="agreementDetails"
                                 render={(arrayHelpers) => (
                                    <div>
                                       {values?.agreementDetails?.map?.((item, index) => {
                                          return (
                                             <div className="row" key={index}>
                                                <div className="col-lg-4" key={index}>
                                                   <CustomFileUpload
                                                      require
                                                      label="Agreement Document"
                                                      {...getFieldProps(
                                                         `agreementDetails[${index}].agreementDocument`
                                                      )}
                                                      onChange={(event, file) =>
                                                         setFieldValue(
                                                            `agreementDetails[${index}].agreementDocument`,
                                                            file
                                                         )
                                                      }
                                                      errors={
                                                         Boolean(
                                                            touched.agreementDetails?.[
                                                               index
                                                            ]?.agreementDocument
                                                         ) &&
                                                         Boolean(
                                                            errors.agreementDetails?.[
                                                               index
                                                            ]?.agreementDocument
                                                         )
                                                      }
                                                      message={
                                                         errors.agreementDetails?.[index]
                                                            ?.agreementDocument
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-3">
                                                   <CustomDatePicker
                                                      require
                                                      label="Start date"
                                                      placeholder="Start date"
                                                      {...getFieldProps(
                                                         `agreementDetails[${index}].startDate`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.agreementDetails?.[
                                                               index
                                                            ]?.startDate
                                                         ) &&
                                                         Boolean(
                                                            errors.agreementDetails?.[
                                                               index
                                                            ]?.startDate
                                                         )
                                                      }
                                                      message={
                                                         errors.agreementDetails?.[index]
                                                            ?.startDate
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-3">
                                                   <CustomDatePicker
                                                      require
                                                      label="End date"
                                                      placeholder="End date"
                                                      {...getFieldProps(
                                                         `agreementDetails[${index}].endDate`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.agreementDetails?.[
                                                               index
                                                            ]?.endDate
                                                         ) &&
                                                         Boolean(
                                                            errors.agreementDetails?.[
                                                               index
                                                            ]?.endDate
                                                         )
                                                      }
                                                      message={
                                                         errors.agreementDetails?.[index]
                                                            ?.endDate
                                                      }
                                                   />
                                                </div>
                                                {values.agreementDetails.length > 1 && (
                                                   <div className="col-lg-2 mt-4 pt-2">
                                                      <button
                                                         type="button"
                                                         className="btn btn-danger"
                                                         onClick={() => {
                                                            arrayHelpers.remove(index);
                                                         }}
                                                      >
                                                         <i className="fas fa-trash"></i>{" "}
                                                         Delete
                                                      </button>
                                                   </div>
                                                )}
                                             </div>
                                          );
                                       })}
                                    </div>
                                 )}
                              />
                           </div>
                           <div className="my-2">
                              <button
                                 type="button"
                                 className="btn mx-1 btn-primary"
                                 onClick={() => {
                                    setFieldValue("agreementDetails", [
                                       ...values.agreementDetails,
                                       {
                                          startDate: "",
                                          endDate: "",
                                          agreementDocument: null,
                                       },
                                    ]);
                                 }}
                              >
                                 <i className="fas fa-plus"></i> Add Agreement Document
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Lane Details</h3>
                  </div>

                  <div className="card-body">
                     <div className="row">
                        {/* Lane name  */}
                        <div className="col-lg-12">
                           <div className="row">
                              <FieldArray
                                 name="location"
                                 render={(arrayHelpers) =>
                                    values.lane.map((item, index) => (
                                       <>
                                          {showLaneTable && (
                                             <h4 className="border-bottom mb-3 pb-2">
                                                {item.laneName}
                                             </h4>
                                          )}
                                          {showLaneTable &&
                                             item.laneName &&
                                             item.origin &&
                                             item.destination &&
                                             item.vehicleType &&
                                             item.tonnage &&
                                             item.expressModeRateAdditional &&
                                             item.superExpressModeRateAdditional &&
                                             item.detentionRateAdditional &&
                                             item.multipleLoadingLocationRateAdditional &&
                                             item.multipleUnloadingLocationRateAdditional &&
                                             item.loadingCharges &&
                                             item.unloadingCharge &&
                                             item.miscellaneousCharges &&
                                             item.brokerageCharges &&
                                             item.miscellaneousRemarks && (
                                                <div className="mt-3 table-responsive">
                                                   <table
                                                      className="table table-bordered"
                                                      style={{ overflow: "scroll" }}
                                                   >
                                                      <thead>
                                                         <tr>
                                                            <th>
                                                               Express Mode Rate
                                                               Additional
                                                            </th>
                                                            <th>
                                                               Super Express Mode Rate
                                                               Additional
                                                            </th>
                                                            <th>
                                                               Detention Rate Additional
                                                            </th>
                                                            <th>
                                                               Multiple Loading Location
                                                               Rate Additional
                                                            </th>
                                                            <th>
                                                               Multiple Unloading Location
                                                               Rate Additional
                                                            </th>
                                                            <th>Loading Charges</th>
                                                            <th>Unloading Charge</th>
                                                            <th>Miscellaneous Charges</th>
                                                            <th>Brokerage Charges</th>
                                                            <th>Miscellaneous Remarks</th>
                                                            <th>Trip Rate</th>
                                                         </tr>
                                                      </thead>
                                                      <tbody>
                                                         <tr>
                                                            <td>
                                                               {
                                                                  item.expressModeRateAdditional
                                                               }
                                                            </td>
                                                            <td>
                                                               {
                                                                  item.superExpressModeRateAdditional
                                                               }
                                                            </td>
                                                            <td>
                                                               {
                                                                  item.detentionRateAdditional
                                                               }
                                                            </td>
                                                            <td>
                                                               {
                                                                  item.multipleLoadingLocationRateAdditional
                                                               }
                                                            </td>
                                                            <td>
                                                               {
                                                                  item.multipleUnloadingLocationRateAdditional
                                                               }
                                                            </td>
                                                            <td>{item.loadingCharges}</td>
                                                            <td>
                                                               {item.unloadingCharge}
                                                            </td>
                                                            <td>
                                                               {item.miscellaneousCharges}
                                                            </td>
                                                            <td>
                                                               {item.brokerageCharges}
                                                            </td>
                                                            <td>
                                                               {item.miscellaneousRemarks}
                                                            </td>
                                                            <td>{item.tripRate}</td>
                                                         </tr>
                                                      </tbody>
                                                   </table>
                                                </div>
                                             )}
                                          {showLaneNameTop === index && (
                                             <>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Customer Name"
                                                      id="laneCustomerName"
                                                      placeholder="Customer Name"
                                                      disabled={true}
                                                      onChange={(event) => {}}
                                                      value={formik.values.customerName}
                                                   />
                                                </div>

                                                <div className="col-lg-4">
                                                   <CustomDropdown
                                                      label="Origin"
                                                      require
                                                      optionData={locationData}
                                                      onChange={(values) => {
                                                         setFieldValue(
                                                            `lane[${index}].origin`,
                                                            values?.[0]?.value
                                                         );
                                                      }}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]?.origin
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]?.origin
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]?.origin
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomDropdown
                                                      label="Destination"
                                                      require
                                                      optionData={locationData}
                                                      values={formik.values.destination}
                                                      onChange={(values) => {
                                                         setFieldValue(
                                                            `lane[${index}].destination`,
                                                            values?.[0]?.value
                                                         );
                                                      }}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.destination
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.destination
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]?.destination
                                                      }
                                                   />
                                                </div>
                                                {/* Vehicle Category Sec */}
                                                <div className="col-lg-6">
                                                   <CustomDropdown
                                                      label="Vehicle Type"
                                                      require
                                                      optionData={vehicleTypeData}
                                                      values={
                                                         formik.values.vehicleCategory
                                                      }
                                                      onChange={(values) => {
                                                         setFieldValue(
                                                            `lane[${index}].vehicleType`,
                                                            values?.[0]?.value
                                                         );
                                                      }}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.vehicleType
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.vehicleType
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]?.vehicleType
                                                      }
                                                   />
                                                </div>
                                                {/* Tonnage Sec */}
                                                <div className="col-lg-6">
                                                   <CustomDropdown
                                                      label="Tonnage(T)"
                                                      require
                                                      optionData={tonnageData}
                                                      values={formik.values.tonnage}
                                                      onChange={(values) => {
                                                         setFieldValue(
                                                            `lane[${index}].tonnage`,
                                                            values?.[0]?.value
                                                         );
                                                      }}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]?.tonnage
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]?.tonnage
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]?.tonnage
                                                      }
                                                   />
                                                </div>
                                                <div className="text-left">
                                                   <button
                                                      type="button"
                                                      className="btn btn-success mr-2"
                                                      onClick={() =>
                                                         handelLaneSave(index)
                                                      }
                                                   >
                                                      Save
                                                   </button>
                                                </div>
                                             </>
                                          )}
                                          {showLaneDetails === index && (
                                             <div className="row mt-3">
                                                <div className="col-lg-12">
                                                   <h4 className="border-bottom mb-3 pb-2">
                                                      {values.customerName &&
                                                         `${values.customerName}`}
                                                      {item.origin &&
                                                         `#${getlabelsFromDropdownData(
                                                            item.origin,
                                                            locationData
                                                         )}`}
                                                      {item.destination &&
                                                         `#${getlabelsFromDropdownData(
                                                            item.destination,
                                                            locationData
                                                         )}`}
                                                      {item.vehicleType &&
                                                         `#${getlabelsFromDropdownData(
                                                            item.vehicleType,
                                                            vehicleTypeData
                                                         )}`}
                                                      {item.tonnage &&
                                                         `#${getlabelsFromDropdownData(
                                                            item.tonnage,
                                                            tonnageData
                                                         )}`}
                                                   </h4>
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Express mode rate additional"
                                                      id="expressModeRateAdditional"
                                                      placeholder="Enter Amount"
                                                      {...getFieldProps(
                                                         `lane[${index}].expressModeRateAdditional`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.expressModeRateAdditional
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.expressModeRateAdditional
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.expressModeRateAdditional
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Super express mode rate additional"
                                                      id="superExpressModeRateAdditional"
                                                      placeholder="Enter amount"
                                                      {...getFieldProps(
                                                         `lane[${index}].superExpressModeRateAdditional`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.superExpressModeRateAdditional
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.superExpressModeRateAdditional
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.superExpressModeRateAdditional
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Detention rate additional"
                                                      id="detentionRateAdditional"
                                                      placeholder="Enter amount"
                                                      {...getFieldProps(
                                                         `lane[${index}].detentionRateAdditional`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.detentionRateAdditional
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.detentionRateAdditional
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.detentionRateAdditional
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Multiple loading location rate additional"
                                                      id="multipleLoadingLocationRateAdditional"
                                                      placeholder="Enter amount"
                                                      {...getFieldProps(
                                                         `lane[${index}].multipleLoadingLocationRateAdditional`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.multipleLoadingLocationRateAdditional
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.multipleLoadingLocationRateAdditional
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.multipleLoadingLocationRateAdditional
                                                      }
                                                   />
                                                </div>

                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Multiple unloading location rate additional"
                                                      id="multipleUnloadingLocationRateAdditional"
                                                      placeholder="Enter amount"
                                                      {...getFieldProps(
                                                         `lane[${index}].multipleUnloadingLocationRateAdditional`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.multipleUnloadingLocationRateAdditional
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.multipleUnloadingLocationRateAdditional
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.multipleUnloadingLocationRateAdditional
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Loading charges"
                                                      id="loadingCharges"
                                                      placeholder="Enter amount"
                                                      {...getFieldProps(
                                                         `lane[${index}].loadingCharges`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.loadingCharges
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.loadingCharges
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.loadingCharges
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Unloading charge"
                                                      id="unloadingCharge"
                                                      placeholder="Enter amount"
                                                      {...getFieldProps(
                                                         `lane[${index}].unloadingCharge`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.unloadingCharge
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.unloadingCharge
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.unloadingCharge
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="miscellaneous charges"
                                                      id="miscellaneousCharges"
                                                      placeholder="Enter amount"
                                                      {...getFieldProps(
                                                         `lane[${index}].miscellaneousCharges`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.miscellaneousCharges
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.miscellaneousCharges
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.miscellaneousCharges
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="brokerage charges"
                                                      id="brokerageCharges"
                                                      placeholder="Enter amount"
                                                      {...getFieldProps(
                                                         `lane[${index}].brokerageCharges`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.brokerageCharges
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.brokerageCharges
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.brokerageCharges
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="miscellaneous Remarks"
                                                      id="miscellaneousRemarks"
                                                      placeholder="Enter remarks"
                                                      {...getFieldProps(
                                                         `lane[${index}].miscellaneousRemarks`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.miscellaneousRemarks
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.miscellaneousRemarks
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.miscellaneousRemarks
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Diesel Liters"
                                                      id="dieselRate"
                                                      placeholder="Enter diesel rate"
                                                      {...getFieldProps(
                                                         `lane[${index}].dieselRate`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.dieselRate
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.dieselRate
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]?.dieselRate
                                                      }
                                                   />
                                                </div>

                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Adblue Liters"
                                                      id="adblueRate"
                                                      placeholder="Enter adblue rate"
                                                      {...getFieldProps(
                                                         `lane[${index}].adblueRate`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.adblueRate
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.adblueRate
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]?.adblueRate
                                                      }
                                                   />
                                                </div>

                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Trip Advance"
                                                      id="tripAdvance"
                                                      placeholder="Enter trip advance"
                                                      {...getFieldProps(
                                                         `lane[${index}].tripAdvance`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.tripAdvance
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.tripAdvance
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]?.tripAdvance
                                                      }
                                                   />
                                                </div>

                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Miscellaneous Rate"
                                                      id="miscellaneousRate"
                                                      placeholder="Enter miscellaneous rate"
                                                      {...getFieldProps(
                                                         `lane[${index}].miscellaneousRate`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.miscellaneousRate
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]
                                                               ?.miscellaneousRate
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]
                                                            ?.miscellaneousRate
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-4">
                                                   <CustomInput
                                                      require
                                                      label="Trip Rate"
                                                      id="tripRate"
                                                      placeholder="Enter trip rate"
                                                      {...getFieldProps(
                                                         `lane[${index}].tripRate`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.lane?.[index]
                                                               ?.tripRate
                                                         ) &&
                                                         Boolean(
                                                            errors.lane?.[index]?.tripRate
                                                         )
                                                      }
                                                      message={
                                                         errors.lane?.[index]?.tripRate
                                                      }
                                                   />
                                                </div>

                                                <div className="col-lg-12"></div>

                                                <div>
                                                   <button
                                                      type="button"
                                                      className="btn btn-secondary mr-2"
                                                      onClick={handelLaneBack}
                                                   >
                                                      <i className="fas fa-arrow-left mr-2"></i>
                                                      Back
                                                   </button>
                                                   <button
                                                      type="button"
                                                      className="btn btn-success mr-2"
                                                      onClick={() => handelSave(index)}
                                                   >
                                                      Save
                                                   </button>
                                                </div>
                                             </div>
                                          )}
                                       </>
                                    ))
                                 }
                              />
                           </div>

                           <div className="text-right">
                              <button
                                 type="button"
                                 className="btn btn-primary float-right"
                                 onClick={handleAddNewLane}
                                 disabled={values.lane.length < 1}
                              >
                                 <i className="fas fa-plus"></i> Add New
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
               <div className="col-12 mt-3 text-center">
                  <button
                     className="btn btn-primary px-4 py-3"
                     type="submit"
                     onClick={handleSubmit}
                  >
                     <h6 className="mb-0 text-uppercase">Submit</h6>
                  </button>
                  {/* <button className="btn btn-danger ml-3 px-4 py-3" type="submit">
                     <h6 className="mb-0 text-uppercase">reset</h6>
                  </button> */}
               </div>
            </div>
         </div>
      </FormikProvider>
   );
}

function getlabelsFromDropdownData(id, data) {
   const label = data?.filter?.((item) => item.value === id)?.[0]?.label;
   return label;
}
